import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";  // Importing useParams to get URL params
import { Card, CardHeader, CardBody, CardFooter } from "../../components/card/card";
import { getAuthHeaders } from "../../misc.js";

const XVID_API_URL = "https://api.moatoverflow.com/v1/xvid";

// Fetch available video types
const fetchXvidTypes = async () => {
  const response = await fetch(`${XVID_API_URL}/types`, {
    headers: { ...getAuthHeaders() },
  });
  return response.json();
};

// Fetch Xvid content by ID
const fetchXvidById = async (xvidId) => {
  const response = await fetch(`${XVID_API_URL}/get/${xvidId}`, {
    headers: { ...getAuthHeaders() },
  });
  return response.json();
};

// Initiate an Xvid object (returns a document with _id and 200 images)
const initiateXvid = async (type) => {
  const response = await fetch(`${XVID_API_URL}/initiate`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify({ type }),
  });
  return response.json();
};

// Generate additional images for the given Xvid (_id) and count
const generateXvidImages = async (xvidId, count) => {
  const response = await fetch(`${XVID_API_URL}/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify({ _id: xvidId, count }),
  });
  return response.json();
};

// Update the Xvid document with the selected images; this sets status to "pending"
const createXvidVideo = async (xvidId, selectedImages) => {
  const response = await fetch(`${XVID_API_URL}/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify({ _id: xvidId, selectedImages }),
  });
  return response.json();
};

// Auto-save function: sends a PUT request to update the Xvid document with the new images array.
const autoSaveXvid = async (xvidId, images) => {
  try {
    await fetch(`${XVID_API_URL}/${xvidId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({ images }),
    });
    // Optionally, you can log success or update UI feedback.
  } catch (error) {
    console.error("Auto save error:", error);
  }
};

const CreateXvid = () => {
  const { id } = useParams();  // Get the ID from the URL path using useParams
  const [xvidTypes, setXvidTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [xvidId, setXvidId] = useState(id || "");  // If an ID is passed, use it as initial state
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Load available Xvid types
    const loadTypes = async () => {
      try {
        const data = await fetchXvidTypes();
        setXvidTypes(data.types);
      } catch (error) {
        console.error("Error fetching Xvid types:", error);
      }
    };
    loadTypes();
  }, []);

  useEffect(() => {
    // If an ID is present in the URL, fetch the Xvid content by ID
    const loadXvidContent = async () => {
      if (xvidId) {
        setIsLoading(true);
        try {
          const data = await fetchXvidById(xvidId);
          if (data._id) {
            setImages(data.images || []);
            setSelectedType(data.type);
          }
        } catch (error) {
          console.error("Error fetching Xvid content:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadXvidContent();
  }, [xvidId]);  // Trigger whenever xvidId changes

  // Handler for initiating the Xvid: generate 200 images and update state.
  const handleInitiateXvid = async () => {
    if (!selectedType) return;
    setIsLoading(true);
    try {
      const data = await initiateXvid(selectedType);
      if (data._id) {
        setXvidId(data._id);
        setImages(data.images);
      }
    } catch (error) {
      console.error("Error initiating Xvid:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for generating additional images.
  const handleGenerateImages = async () => {
    if (!xvidId) return;
    setIsLoading(true);
    try {
      const data = await generateXvidImages(xvidId, 5);  // Example: generating 5 images
      setImages(data.images);
    } catch (error) {
      console.error("Error generating images:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for finalizing the Xvid: send selected image indices and update status to pending.
  const handleCreateVideo = async () => {
    if (!xvidId) return;
    setIsLoading(true);
    try {
      const selectedImages = images.filter((img) => img.selected);  // Only selected images
      const data = await createXvidVideo(xvidId, selectedImages);
      console.log("Xvid created:", data);
    } catch (error) {
      console.error("Error updating Xvid:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to toggle the selection state of an image and trigger auto-save.
  const toggleImageSelection = (imageId) => {
    // Create a new images array with the updated selection state.
    const newImages = images.map((img) =>
      img._id === imageId ? { ...img, selected: !img.selected } : img
    );
    setImages(newImages);

    // Auto-save immediately after selection change.
    if (xvidId) {
      autoSaveXvid(xvidId, newImages);
    }
  };

  return (
    <Card>
      <CardHeader>
        <h1>{xvidId ? "Edit Xvid" : "Create Xvid"}</h1>
      </CardHeader>
      <CardBody>
        {!xvidId && (
          <div className="mb-3">
            <label>Video Type</label>
            <select
              className="form-select"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              disabled={isLoading}
            >
              <option value="">Select Video Type</option>
              {xvidTypes.map((type) => (
                <option key={type.type} value={type.type}>
                  {type.title}
                </option>
              ))}
            </select>
            <button
              className="btn btn-success mt-2"
              onClick={handleInitiateXvid}
              disabled={!selectedType || isLoading}
            >
              {isLoading ? "Generating..." : "Generate"}
            </button>
          </div>
        )}

        {xvidId && (
          <Card className="mb-3">
            <CardHeader>
              <h3>Images</h3>
              <button
                className="btn btn-primary"
                onClick={handleGenerateImages}
                disabled={isLoading}
              >
                {isLoading ? "Generating..." : "Generate Images"}
              </button>
            </CardHeader>
            <CardBody>
              <div className="row">
                {images.map((img) => (
                  <div
                    key={img._id}
                    className="col-3 mb-3"
                    onClick={() => toggleImageSelection(img._id)}
                    style={{
                      border: img.selected ? "2px solid green" : "1px solid #ccc",
                      cursor: "pointer",
                      padding: "5px",
                      textAlign: "center",
                    }}
                  >
                    {img.status === "pending" && <div>Generating...</div>}
                    {img.status === "failed" && <div>Failed</div>}
                    {img.status === "success" && (
                      <img
                        src={`https://api.moatoverflow.com/xvid/images/${img._id}.png`}
                        alt={img.prompt}
                        className="img-fluid"
                      />
                    )}
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}
      </CardBody>
      <CardFooter className="d-flex justify-content-between">
        <div>Selected: {images.filter((img) => img.selected).length}</div>
        <button
          className="btn btn-primary"
          onClick={handleCreateVideo}
          disabled={isLoading}
        >
          {isLoading ? "Updating..." : "Create Video"}
        </button>
      </CardFooter>
    </Card>
  );
};

export default CreateXvid;
